import React from 'react';
import { Lightbulb, TrendingUp, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';

interface HealthStats {
  totalLogs?: number;
  seizureFreeDays?: number;
  therapySessions?: number;
  medicationAdherence?: number;
  insights?: Insight[];
}

interface Insight {
  id: string;
  type: 'positive' | 'warning' | 'info';
  message: string;
}

interface HealthInsightsWidgetProps {
  stats?: HealthStats | null;
  isLoading?: boolean;
}

function generateInsights(stats?: HealthStats | null): Insight[] {
  if (!stats) return [];
  
  const insights: Insight[] = [];

  // Medication adherence insights
  if (stats.medicationAdherence !== undefined) {
    if (stats.medicationAdherence >= 90) {
      insights.push({
        id: 'med-excellent',
        type: 'positive',
        message: `Great job! Medication adherence is at ${stats.medicationAdherence}%.`,
      });
    } else if (stats.medicationAdherence < 70) {
      insights.push({
        id: 'med-warning',
        type: 'warning',
        message: `Medication adherence is at ${stats.medicationAdherence}%. Try setting reminders.`,
      });
    }
  }

  // Seizure-free insights
  if (stats.seizureFreeDays !== undefined) {
    if (stats.seizureFreeDays >= 30) {
      insights.push({
        id: 'seizure-milestone',
        type: 'positive',
        message: `Amazing! ${stats.seizureFreeDays} days seizure-free.`,
      });
    } else if (stats.seizureFreeDays >= 7) {
      insights.push({
        id: 'seizure-progress',
        type: 'info',
        message: `${stats.seizureFreeDays} days seizure-free. Keep tracking!`,
      });
    }
  }

  // Therapy insights
  if (stats.therapySessions !== undefined && stats.therapySessions > 0) {
    insights.push({
      id: 'therapy-active',
      type: 'info',
      message: `${stats.therapySessions} therapy sessions logged this month.`,
    });
  }

  // Default insight if none generated
  if (insights.length === 0) {
    insights.push({
      id: 'keep-logging',
      type: 'info',
      message: 'Keep logging to unlock personalized insights.',
    });
  }

  return insights.slice(0, 3); // Max 3 insights
}

function InsightIcon({ type }: { type: Insight['type'] }) {
  switch (type) {
    case 'positive':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    default:
      return <Lightbulb className="w-5 h-5 text-blue-500" />;
  }
}

export function HealthInsightsWidget({
  stats,
  isLoading,
}: HealthInsightsWidgetProps): React.ReactElement {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-200 rounded w-1/3" />
          <div className="h-16 bg-gray-200 rounded" />
          <div className="h-16 bg-gray-200 rounded" />
        </div>
      </Card>
    );
  }

  const insights = stats?.insights || generateInsights(stats);

  return (
    <Card className="p-6">
      <h3 className="font-display font-semibold text-navy mb-4 flex items-center">
        <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
        Health Insights
      </h3>
      
      <div className="space-y-3">
        {insights.map((insight) => (
          <div
            key={insight.id}
            className={`
              flex items-start gap-3 p-3 rounded-lg
              ${insight.type === 'positive' ? 'bg-green-50' : ''}
              ${insight.type === 'warning' ? 'bg-yellow-50' : ''}
              ${insight.type === 'info' ? 'bg-blue-50' : ''}
            `}
          >
            <InsightIcon type={insight.type} />
            <p className="text-sm text-gray-700 flex-1">
              {insight.message}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default HealthInsightsWidget;
